import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { comparisonService } from '@/services/comparisonService'
import type { SearchableDimensionType, SearchResultItem } from '@/types/comparison'

/**
 * Hook for searching subjects via autocomplete.
 *
 * Supports fuzzy search for: brand, team, hashtag, channel, author, post.
 *
 * @param type - Dimension type to search
 * @param query - Search query (min 2 characters)
 * @param options - Optional configuration
 * @returns React Query result with search results
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSubjectSearch('brand', 'nike', {
 *   limit: 10,
 *   enabled: query.length >= 2,
 * })
 *
 * return (
 *   <div>
 *     {isLoading && <p>Searching...</p>}
 *     {data?.data.map((item) => (
 *       <div key={item.id}>{item.name}</div>
 *     ))}
 *   </div>
 * )
 * ```
 */
export function useSubjectSearch(
  type: SearchableDimensionType,
  query: string,
  options?: {
    /**
     * Max results (default: 20, max: 50).
     */
    limit?: number

    /**
     * Enable/disable the query (default: true).
     * Useful for conditional fetching (e.g., only when query.length >= 2).
     */
    enabled?: boolean

    /**
     * Stale time in milliseconds (default: 5 minutes).
     * How long the cached data is considered fresh.
     */
    staleTime?: number

    /**
     * Minimum query length before triggering search (default: 2).
     * Set to 0 to allow empty-query searches (e.g., for channels).
     */
    minQueryLength?: number
  }
) {
  const { limit = 20, enabled = true, staleTime = 5 * 60 * 1000, minQueryLength = 2 } = options || {}

  return useQuery({
    queryKey: ['subjectSearch', type, query, limit],
    queryFn: async () => {
      // Validate query length
      if (query.length < minQueryLength) {
        return { success: true, data: [] } as const
      }

      return await comparisonService.searchSubjects({
        type,
        q: query,
        limit,
      })
    },
    enabled: enabled && query.length >= minQueryLength,
    staleTime,
    // Retry on failure (3 times with exponential backoff)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

/**
 * Hook variant with debouncing for real-time autocomplete.
 *
 * Debounces the query input to avoid excessive API calls.
 *
 * @param type - Dimension type to search
 * @param query - Search query (will be debounced)
 * @param debounceMs - Debounce delay in milliseconds (default: 300ms)
 * @param options - Optional configuration
 * @returns React Query result with search results
 *
 * @example
 * ```tsx
 * const [query, setQuery] = useState('')
 * const { data, isLoading } = useDebouncedSubjectSearch(
 *   'brand',
 *   query,
 *   300, // 300ms debounce
 *   { limit: 10 }
 * )
 *
 * return (
 *   <input
 *     value={query}
 *     onChange={(e) => setQuery(e.target.value)}
 *     placeholder="Search brands..."
 *   />
 * )
 * ```
 */
export function useDebouncedSubjectSearch(
  type: SearchableDimensionType,
  query: string,
  debounceMs: number = 300,
  options?: {
    limit?: number
    enabled?: boolean
    staleTime?: number
    minQueryLength?: number
  }
) {
  const [debouncedQuery, setDebouncedQuery] = React.useState(query)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query)
    }, debounceMs)

    return () => {
      clearTimeout(handler)
    }
  }, [query, debounceMs])

  return useSubjectSearch(type, debouncedQuery, options)
}

/**
 * Helper: Extract search results from query response.
 *
 * @param data - Query response data
 * @returns Array of search result items, or empty array if no data
 */
export function getSearchResults(
  data: ReturnType<typeof useSubjectSearch>['data']
): SearchResultItem[] {
  return data?.data ? [...data.data] : []
}
